import { isDefined } from "@redotech/util/type";
import * as z from "zod";
import type {
  OperationsSettingsPatch,
  ReturnsOperationsSettingsSource,
} from "./operations-settings-source";

export const shippingOptionsSettingsShape = {
  /** Requires SET_SHIPPING_SETTINGS. */
  shipping: z.object({
    pickupMaxCoveredAmountEnabled: z.boolean(),
    pickupMaxCoveredAmount: z.number().min(0).nullable(),
    signatureEnabled: z.boolean(),
    /** USD regardless of shop currency. */
    signatureThreshold: z.number().min(0).nullable(),
    signatureForTagsEnabled: z.boolean(),
    signatureForTagsTags: z.array(z.string()),
    ignoreTeamCarrierOverrideInMerchantDash: z.boolean(),
  }),
};

export type ShippingOptionsSettings = z.infer<
  z.ZodObject<typeof shippingOptionsSettingsShape>
>;

export function shippingOptionsSettingsFromTeam({
  settings,
}: ReturnsOperationsSettingsSource): ShippingOptionsSettings {
  const { returns } = settings;
  return {
    shipping: {
      pickupMaxCoveredAmountEnabled: !!settings.pickupMaxCoveredAmountEnabled,
      pickupMaxCoveredAmount: settings.pickupMaxCoveredAmount ?? null,
      signatureEnabled: !!returns?.requireReturnShippingSignature?.enabled,
      signatureThreshold:
        returns?.requireReturnShippingSignature?.threshold ?? null,
      signatureForTagsEnabled:
        !!returns?.requireReturnShippingSignatureForTags?.enabled,
      signatureForTagsTags:
        returns?.requireReturnShippingSignatureForTags?.tags ?? [],
      ignoreTeamCarrierOverrideInMerchantDash:
        settings.ignoreTeamCarrierOverrideInMerchantDash ?? false,
    },
  };
}

export function shippingOptionsTeamPatch(
  { settings }: ReturnsOperationsSettingsSource,
  next: ShippingOptionsSettings,
): OperationsSettingsPatch {
  const { shipping } = next;
  const set: Record<string, unknown> = {
    pickupMaxCoveredAmountEnabled: shipping.pickupMaxCoveredAmountEnabled,
    "returns.requireReturnShippingSignature.enabled": shipping.signatureEnabled,
    "returns.requireReturnShippingSignatureForTags.enabled":
      shipping.signatureForTagsEnabled,
    "returns.requireReturnShippingSignatureForTags.tags":
      shipping.signatureForTagsTags,
    ignoreTeamCarrierOverrideInMerchantDash:
      shipping.ignoreTeamCarrierOverrideInMerchantDash,
  };
  const unset: string[] = [];

  if (shipping.pickupMaxCoveredAmount !== null) {
    set.pickupMaxCoveredAmount = shipping.pickupMaxCoveredAmount;
  } else if (isDefined(settings.pickupMaxCoveredAmount)) {
    unset.push("pickupMaxCoveredAmount");
  }
  if (shipping.signatureThreshold !== null) {
    set["returns.requireReturnShippingSignature.threshold"] =
      shipping.signatureThreshold;
  } else if (
    isDefined(settings.returns?.requireReturnShippingSignature?.threshold)
  ) {
    unset.push("returns.requireReturnShippingSignature.threshold");
  }

  return { set, unset };
}
